import { useMutation } from "@apollo/client";
import { useModal } from "@ebay/nice-modal-react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import FlashContext from "components/FlashMessages/FlashContext";
import FormFieldSpace from "components/FormFields/Space";
import FormFieldTags from "components/FormFields/Tags";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import Textarea from "ds/components/Textarea";
import useTypedContext from "hooks/useTypedContext";
import { BlueprintVersionedGroup } from "types/generated";
import { getDrawerFormFix } from "utils/css";
import DrawerSection from "ds/components/Drawer/Section";
import Typography from "ds/components/Typography";
import { stringIsRequired } from "utils/formValidators";

import { REFETCH_QUERIES_LIST } from "../constants";
import { CREATE_BLUEPRINT_GROUP, UPDATE_BLUEPRINT_GROUP } from "../gql";
import {
  CreateTemplateDrawerProps,
  CreateTemplateFormFields,
  UpdateTemplateFormFields,
} from "./types";
import useTemplateAvailableSpaces from "../hooks/useTemplateAvailableSpaces";

const CreateTemplateDrawer = createDrawer(
  ({ template, refetchQueries }: CreateTemplateDrawerProps) => {
    const drawer = useModal();

    const isEditMode = !!template;

    const availableSpaces = useTemplateAvailableSpaces(isEditMode);

    const navigate = useNavigate();

    const { onError, reportSuccess } = useTypedContext(FlashContext);

    const templateName = template?.name;

    const builderForm = useForm<CreateTemplateFormFields>({
      defaultValues: {
        name: template ? templateName : "",
        description: template?.description || "",
        space: template?.space.id || "",
        labels: template?.labels.map((value) => ({ value })) || [],
        ...(!isEditMode && {
          initialVersion: {
            description: "",
            number: "",
          },
        }),
      },
      mode: "onChange",
    });

    const {
      register,
      handleSubmit,
      formState: { errors, isDirty },
    } = builderForm;

    const [createTemplate] = useMutation(CREATE_BLUEPRINT_GROUP, {
      refetchQueries: refetchQueries || REFETCH_QUERIES_LIST,
    });

    const [updateTemplate] = useMutation(UPDATE_BLUEPRINT_GROUP, {
      refetchQueries: refetchQueries || REFETCH_QUERIES_LIST,
    });

    const onCreateSubmit = (formData: CreateTemplateFormFields) => {
      createTemplate({
        variables: {
          input: {
            name: formData.name,
            description: formData.description,
            space: formData.space,
            labels: formData.labels.map((item) => item.value),
            initialVersion: formData.initialVersion,
          },
        },
      })
        .then(({ data }) => {
          if (data?.blueprintVersionedGroupCreate) {
            reportSuccess({
              message: `Template "${data.blueprintVersionedGroupCreate.name}" is successfully created`,
            });

            drawer.hide();
            navigate(`/template/${data.blueprintVersionedGroupCreate.id}`);
          }
        })
        .catch(onError);
    };

    const onEditSubmit = (
      formData: UpdateTemplateFormFields,
      templateItem: BlueprintVersionedGroup
    ) => {
      updateTemplate({
        variables: {
          id: templateItem.id,
          input: {
            name: formData.name,
            description: formData.description,
            space: formData.space,
            labels: formData.labels.map((item) => item.value),
          },
        },
      })
        .then(({ data }) => {
          if (data?.blueprintVersionedGroupUpdate) {
            reportSuccess({
              message: `Template "${data.blueprintVersionedGroupUpdate.name}" is successfully saved`,
            });

            drawer.hide();
          }
        })
        .catch(onError);
    };

    const onSubmit: SubmitHandler<CreateTemplateFormFields> = (formData) => {
      if (isEditMode && template?.id) {
        onEditSubmit(formData, template);
      } else {
        onCreateSubmit(formData);
      }
    };

    return (
      <DrawerForm isDirty={isDirty}>
        <FormProvider {...builderForm}>
          <form onSubmit={handleSubmit(onSubmit)} {...getDrawerFormFix()}>
            <DrawerHeader justify="between">
              {!isEditMode && <DrawerHeaderTitle title="Create template" />}
              {isEditMode && <DrawerHeaderTitle title="Edit template" />}
              <DrawerCloseIcon />
            </DrawerHeader>
            <DrawerBody fullHeight noPadding>
              <DrawerSection>
                <FormField label="Name" error={errors?.name?.message}>
                  {({ ariaInputProps }) => (
                    <Input
                      placeholder="Enter template name here..."
                      error={!!errors?.name}
                      {...register("name", { required: "Name field is required." })}
                      {...ariaInputProps}
                    />
                  )}
                </FormField>

                <FormFieldSpace spaces={availableSpaces} />

                <FormField label="Description" isOptional>
                  {({ ariaInputProps }) => (
                    <Textarea
                      placeholder="Enter template description here..."
                      {...register("description")}
                      {...ariaInputProps}
                    />
                  )}
                </FormField>

                <FormFieldTags label="Labels" tagName="label" name="labels" />
              </DrawerSection>
              {!isEditMode && (
                <DrawerSection>
                  <Typography tag="p" variant="p-t6">
                    Initial version
                  </Typography>
                  <FormField
                    label="Unique version identifier"
                    error={errors?.initialVersion?.number?.message}
                    tooltipInfo="A unique identifier for this version, e.g., 1.0.0"
                  >
                    {({ ariaInputProps }) => (
                      <Input
                        placeholder="0.0.1"
                        error={!!errors?.initialVersion?.number}
                        {...register("initialVersion.number", {
                          validate: stringIsRequired("Version identifier is required."),
                        })}
                        {...ariaInputProps}
                      />
                    )}
                  </FormField>
                  <FormField
                    label="Deployment instruction"
                    isOptional
                    helperText="Supports Markdown"
                  >
                    {({ ariaInputProps }) => (
                      <Textarea
                        placeholder="Instructions for deploying this version..."
                        {...register("initialVersion.description")}
                        {...ariaInputProps}
                      />
                    )}
                  </FormField>
                </DrawerSection>
              )}
              <DrawerFooter>
                <DrawerFooterActions>
                  <DrawerCancelButton />
                  <Button variant="primary" type="submit">
                    {isEditMode ? "Save" : "Create"}
                  </Button>
                </DrawerFooterActions>
              </DrawerFooter>
            </DrawerBody>
          </form>
        </FormProvider>
      </DrawerForm>
    );
  }
);

export const showCreateTemplateDrawer = createDrawerTrigger(CreateTemplateDrawer);
